import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import FirestoreService from '../assets/js/firestore_instance';

const Modal = ({ isOpen, onClose, children }) => {
    const modalRef = useRef();
  
    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape') onClose();
      };
  
      const handleClickOutside = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          onClose();
        }
      };
  
      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        document.addEventListener('mousedown', handleClickOutside);
      }
  
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onClose]);
  
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
            <motion.div
              ref={modalRef}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-gray-900 rounded-xl p-6 max-w-md mx-auto z-50"
              role="dialog"
              aria-modal="true"
            >
              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

const AdminSection = ({organization}) => {
  const [admins, setAdmins] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const db = FirestoreService.getFirestore();


  const handleSubmit = (e) => {
    e.preventDefault();
    addAdmin();
  };
  

  useEffect(() => {
    fetchAdmins();
  }, []);

const fetchAdmins = async () => {
  try {
    setLoading(true);
    const q = query(
      collection(db, "users"),
      where("isOrgAdmin", "array-contains", organization.organizationId)
    );
    const querySnapshot = await getDocs(q);
    const adminsList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setAdmins(adminsList);
  } catch (err) {
    setError(err.message);
    toast.error('Failed to load organization admins');
  } finally {
    setLoading(false);
  }
};

const addAdmin = async () => {
  try {
    // Find user by email
    const userQuery = query(
      collection(db, "users"),
      where("email", "==", newAdminEmail)
    );
    const userSnapshot = await getDocs(userQuery);

    if (userSnapshot.empty) {
      toast.error('User not found');
      return;
    }

    const userDoc = userSnapshot.docs[0];
    const userData = userDoc.data();

    // Add organization ID to isOrgAdmin array
    const newOrgAdmins = [...(userData.isOrgAdmin || [])];
    if (!newOrgAdmins.includes(organization.organizationId)) {
      newOrgAdmins.push(organization.organizationId);
    }

    // Update user document
    await updateDoc(doc(db, "users", userDoc.id), {
      isOrgAdmin: newOrgAdmins
    });

    toast.success('Admin added successfully');
    setIsAddModalOpen(false);
    setNewAdminEmail('');
    fetchAdmins();
  } catch (err) {
    console.error(err);
    toast.error('Failed to add admin');
  }
};

const removeAdmin = async (adminId) => {
  try {
    const userDoc = doc(db, "users", adminId);
    const userData = (await getDoc(userDoc)).data();

    // Remove organization ID from isOrgAdmin array
    const newOrgAdmins = userData.isOrgAdmin.filter(
      id => id !== organization.organizationId
    );

    // Update user document
    await updateDoc(userDoc, {
      isOrgAdmin: newOrgAdmins
    });

    toast.success('Admin removed successfully');
    fetchAdmins();
  } catch (err) {
    console.error(err);
    toast.error('Failed to remove admin');
  }
};

  

  const filteredAdmins = admins.filter(admin => 
    admin.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 bg-gray-900 rounded-xl shadow-xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Manage Admins</h1>
            <p className="text-gray-400">Promote someone to an Admin User on the JoltChat app!</p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Admin
          </button>
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search admins..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 text-center py-4">{error}</div>
        ) : (
          <div className="grid gap-4">
            <AnimatePresence>
              {filteredAdmins.map((admin) => (
                <motion.div
                  key={admin.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-gray-800 p-4 rounded-lg flex justify-between items-center"
                >
                <div className='flex items-center'>
                    <img src={admin.photourl} alt={admin.name} className="w-14 h-14 rounded-full mr-4" />
                  <div>
                    <p className="text-white">{admin.name}</p>
                    <p className="text-sm text-gray-400">{admin.email}</p>
                  </div>
                </div>
                  <button
                    onClick={() => removeAdmin(admin.id)}
                    className="text-red-500 hover:text-red-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      >
        <h2 className="text-xl font-bold text-white mb-4">Add New Admin</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Enter email address"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 mb-4"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg"
            >
              Add Admin
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminSection;